<template>
  <div class="navbar bg-base-100 shadow-sm border-b border-base-300 relative z-50 min-h-16">
    <div class="navbar-start w-auto flex-1 min-w-0">
      <!-- Mobile menu button: key by route so dropdown resets on navigation and doesn't stay open/stale -->
      <div v-if="authStore.isAuthenticated" class="dropdown lg:hidden" :key="String($route.path)">
        <div tabindex="0" role="button" class="btn btn-ghost">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h8m-8 6h16"
            />
          </svg>
        </div>
        <ul
          tabindex="0"
          class="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-60"
        >
          <li>
            <router-link to="/" :class="{ active: $route.path === '/' }">
              <HomeIcon class="w-4 h-4" />
              Dashboard
            </router-link>
          </li>
          <li>
            <router-link to="/users" :class="{ active: $route.path.startsWith('/users') || $route.path.startsWith('/roles') || $route.path.startsWith('/permissions') }">
              <UserGroupIcon class="w-4 h-4" /> Users
            </router-link>
          </li>
          <li>
            <router-link to="/agencies" :class="{ active: $route.path.startsWith('/agencies') }">
              <BuildingOfficeIcon class="w-4 h-4" /> Agencies
            </router-link>
          </li>
          <li>
            <router-link to="/properties" :class="{ active: $route.path.startsWith('/properties') }"
              ><HomeModernIcon class="w-4 h-4" /> Properties</router-link
            >
          </li>
          <li class="menu-title px-2 py-1 text-xs uppercase opacity-70">
            <span class="flex items-center gap-2">
              <EnvelopeOpenIcon class="w-4 h-4" /> Leads
            </span>
          </li>
          <li class="pl-2">
            <router-link
              to="/leads"
              :class="{
                active:
                  $route.path === '/leads' ||
                  ($route.path.startsWith('/leads/') && $route.path !== '/leads/broker-contact'),
              }"
            >
              List
            </router-link>
          </li>
          <li class="pl-2">
            <router-link
              to="/leads/broker-contact"
              :class="{ active: $route.path === '/leads/broker-contact' }"
            >
              Broker lead contact
            </router-link>
          </li>
          <li>
            <router-link
              to="/property-card-ads"
              :class="{ active: $route.path.startsWith('/property-card-ads') }"
              ><MegaphoneIcon class="w-4 h-4" /> Ads</router-link
            >
          </li>
          <li>
            <router-link
              to="/search-order-config"
              :class="{ active: $route.path.startsWith('/search-order-config') }"
              ><FunnelIcon class="w-4 h-4" /> Search Config</router-link
            >
          </li>
          <li>
            <router-link to="/paid-extras" :class="{ active: $route.path.startsWith('/paid-extras') }">
              <BanknotesIcon class="w-4 h-4" /> Paid Extras
            </router-link>
          </li>
          <li>
            <router-link to="/assets" :class="{ active: $route.path.startsWith('/assets') }"
              ><PhotoIcon class="w-4 h-4" /> Assets</router-link
            >
          </li>
          <li>
            <router-link
              to="/integrations"
              :class="{ active: $route.path.startsWith('/integrations') }"
              ><Cog6ToothIcon class="w-4 h-4" /> Integrations</router-link
            >
          </li>
        </ul>
      </div>

      <!-- Logo/Brand -->
      <router-link to="/" class="btn btn-ghost text-xl font-bold">
        <BostelloLogo size="sm" class-name="h-8 w-auto" />
        Admin
      </router-link>
    </div>

    <!-- Center - Desktop Navigation (plain flex; avoid DaisyUI menu-horizontal + tooltip clipping) -->
    <div
      v-if="showDesktopNav"
      class="navbar-center hidden lg:flex flex-none flex-nowrap items-center justify-center gap-0.5 px-1"
    >
      <router-link
        to="/"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path === '/' }"
        title="Dashboard"
        aria-label="Dashboard"
      >
        <HomeIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/users"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{
          'btn-active':
            $route.path.startsWith('/users') ||
            $route.path.startsWith('/roles') ||
            $route.path.startsWith('/permissions'),
        }"
        title="Users"
        aria-label="Users"
      >
        <UserGroupIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/agencies"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/agencies') }"
        title="Agencies"
        aria-label="Agencies"
      >
        <BuildingOfficeIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/properties"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/properties') }"
        title="Properties"
        aria-label="Properties"
      >
        <HomeModernIcon class="w-5 h-5" />
      </router-link>

      <div class="dropdown dropdown-end shrink-0">
        <div
          tabindex="0"
          role="button"
          class="btn btn-ghost btn-sm gap-0.5"
          :class="{ 'btn-active': $route.path.startsWith('/leads') }"
          title="Leads"
          aria-label="Leads"
          aria-haspopup="true"
        >
          <EnvelopeOpenIcon class="w-5 h-5" />
          <ChevronDownIcon class="h-4 w-4 opacity-70" />
        </div>
        <ul
          tabindex="0"
          class="dropdown-content z-[60] menu p-2 shadow bg-base-100 rounded-box w-56 mt-2 border border-base-300"
        >
          <li>
            <router-link
              to="/leads"
              :class="{
                active:
                  $route.path === '/leads' ||
                  ($route.path.startsWith('/leads/') && $route.path !== '/leads/broker-contact'),
              }"
            >
              List
            </router-link>
          </li>
          <li>
            <router-link
              to="/leads/broker-contact"
              :class="{ active: $route.path === '/leads/broker-contact' }"
            >
              Broker lead contact (Mautic)
            </router-link>
          </li>
        </ul>
      </div>

      <router-link
        to="/property-card-ads"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/property-card-ads') }"
        title="Ads"
        aria-label="Ads"
      >
        <MegaphoneIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/search-order-config"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/search-order-config') }"
        title="Search Config"
        aria-label="Search Config"
      >
        <FunnelIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/paid-extras"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/paid-extras') }"
        title="Paid Extras"
        aria-label="Paid Extras"
      >
        <BanknotesIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/assets"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/assets') }"
        title="Assets"
        aria-label="Assets"
      >
        <PhotoIcon class="w-5 h-5" />
      </router-link>

      <router-link
        to="/integrations"
        class="btn btn-ghost btn-sm shrink-0"
        :class="{ 'btn-active': $route.path.startsWith('/integrations') }"
        title="Integrations"
        aria-label="Integrations"
      >
        <Cog6ToothIcon class="w-5 h-5" />
      </router-link>
    </div>

    <!-- Right side - Actions -->
    <div class="navbar-end w-auto flex-1 min-w-0 justify-end">
      <div class="flex items-center gap-2">
        <!-- Bostello.com link -->
        <a
          href="https://bostello.com"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-ghost btn-sm"
          title="Go to Bostello.com"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
            />
          </svg>
        </a>

        <!-- User menu (only show when authenticated) -->
        <div v-if="authStore.isAuthenticated" class="dropdown dropdown-end">
          <div tabindex="0" role="button" class="btn btn-ghost btn-sm">
            <div class="avatar placeholder">
              <div class="bg-primary text-primary-content rounded-full w-8">
                <span class="text-xs">
                  {{
                    (authStore.user?.firstName || authStore.user?.email || 'U')[0]?.toUpperCase()
                  }}
                </span>
              </div>
            </div>
            <span class="hidden md:block text-sm font-medium">
              {{
                authStore.user?.firstName || authStore.user?.lastName
                  ? [authStore.user?.firstName, authStore.user?.lastName].filter(Boolean).join(' ')
                  : authStore.user?.email
              }}
            </span>
            <ChevronDownIcon class="h-4 w-4" />
          </div>
          <ul
            tabindex="0"
            class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52"
          >
            <li class="menu-title">
              <span>Signed in as</span>
            </li>
            <li class="disabled">
              <span class="font-medium">
                {{
                  authStore.user?.firstName || authStore.user?.lastName
                    ? [authStore.user?.firstName, authStore.user?.lastName]
                        .filter(Boolean)
                        .join(' ')
                    : authStore.user?.email
                }}
              </span>
            </li>
            <li>
              <router-link to="/settings">Settings</router-link>
            </li>
            <li>
              <button @click="handleSignOut" class="text-error">Sign out</button>
            </li>
          </ul>
        </div>

        <!-- Sign in button (only show when not authenticated) -->
        <router-link v-else to="/login" class="btn btn-primary btn-sm"> Sign in </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    ChevronDownIcon,
    HomeIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    HomeModernIcon,
    EnvelopeOpenIcon,
    MegaphoneIcon,
    FunnelIcon,
    BanknotesIcon,
    PhotoIcon,
    Cog6ToothIcon,
  } from '@heroicons/vue/24/outline'
  import { useAuthStore } from '@/stores/auth'
  import { useToast } from 'vue-toastification'
  import BostelloLogo from '@/components/BostelloLogo.vue'

  const props = withDefaults(defineProps<{ showDesktopNav?: boolean }>(), {
    showDesktopNav: true,
  })
  const showDesktopNav = computed(() => props.showDesktopNav)

  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const toast = useToast()

  // Close open DaisyUI dropdowns after navigation so hover/ focus state cannot clip siblings
  watch(
    () => route.path,
    () => {
      const el = document.activeElement
      if (el instanceof HTMLElement) el.blur()
    }
  )

  const showUserMenu = ref(false)

  const handleSignOut = async () => {
    try {
      await authStore.signOut()
      toast.success('Signed out successfully')
      router.push('/login')
    } catch (error: any) {
      console.error('Sign out error:', error)

      // Check if this is a timeout error
      if (error?.message?.includes('timed out')) {
        // For timeout errors, offer to force sign out locally
        const forceSignOut = confirm(
          'Sign out timed out. Your session may still be active on the server.\n\n' +
          'Click OK to sign out locally anyway, or Cancel to stay signed in.'
        )

        if (forceSignOut) {
          try {
            await authStore.forceSignOut()
            toast.success('Signed out locally')
            router.push('/login')
          } catch (forceError) {
            console.error('Force sign out error:', forceError)
            toast.error('Failed to sign out locally')
          }
        }
      } else {
        toast.error('Failed to sign out')
      }
    }
  }

  // Close dropdown when clicking outside (be safe if target isn't an Element)
  const handleClickOutside = (event: Event) => {
    const target = event.target as Node | null
    if (!(target instanceof Element)) return
    if (!target.closest('.navbar')) {
      showUserMenu.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>
